import type { Scene } from '@babylonjs/core/scene';

import type { AssistantController } from '../assistant/AssistantController';
import type { AuthoringController } from '../authoring/AuthoringController';
import type { GrabberHub } from '../input/Grabbers';
import { quatToArray, vec3ToArray } from '../net/packing';

/**
 * Mouse and keyboard stand-in for hands, so the whole authoring model works on a desktop.
 *
 * Not a convenience. Two browser tabs used to be the only practical way to exercise presence,
 * locks and pose streaming without two headsets; they no longer can, now that a desktop session
 * joins as a spectator unconditionally and never starts authoring (`854e193` — see this file's
 * own note below, "a desktop session is spectator-only in the product regardless"). What this
 * class still is: the only way the automated suite exercises grab, lock and connection gestures
 * at all without a headset, since roughly a dozen tests drive `AuthoringController` and this
 * grabber directly under `NullEngine`, bypassing `main.ts`'s spectator gate entirely. Two
 * browser tabs are still the way to prove presence and pose streaming; README.md's own "Testing
 * multiplayer without two headsets" has the corrected account.
 *
 *   drag on a node          grab and move it — near, resolved by reach
 *   shift-drag from a node  draw a connection to another node — far, resolved by aim
 *   tab                     open / close the tray
 *   delete                  delete the node you are holding
 *   p                       cycle tools — build to draw and back — the desktop's own mode
 *                           switch (plan/history/control-modes.md), in keeping with Tab for the tray
 *
 * No key here reaches the dominant stick's own axes — draw's four-position variant cycle
 * (slim/medium/fat marker, eraser) and its ink-colour cycle both live on a thumbstick this class
 * has no equivalent for (`ToolMode.cycleDrawVariant`/`cyclePenColor`, `ModeDefinitions.ts`).
 * `plan/mode-hierarchy.md`'s own "Desktop — open question, not a decision" leaves this open
 * rather than deciding it, and a desktop session is spectator-only in the product regardless
 * (`854e193`) — so this is left genuinely unbound here, not given some other key standing in for
 * a stick it does not have.
 *
 *   v (hold)                the assistant's talk button (X on the off hand) — push-to-talk;
 *                           holding down `v` opens a turn, letting go closes it, the same
 *                           press/release shape a real held button gives for free and a
 *                           `keydown`-only listener does not, so this key is read on both
 *                           `keydown` and `keyup`
 *   m                       toggle the microphone (Y on the off hand)
 *   scroll, over a wheel    spin the tray's carousel by one item (plan/tray-categories.md)
 *
 * **Both gestures are the trigger now** (plan/mode-hierarchy.md, "Grabbing moves from the
 * dominant grip to the dominant trigger; near/far is gone" — settled 2026-08-10): a real
 * controller reads one button for both grab and connect. That was disambiguated by
 * `nodeWithinReach`'s proximity test (`GrabReach.ts`) for one day, as a stopgap ahead of Link
 * mode existing; Link mode's own gesture retired it the same day (`ConnectionDrawer.ts`'s own
 * module doc) — grabbing and linking are confined to disjoint modes now, so which mode you are
 * in disambiguates a press, not proximity. `updateGrabber` below still reports every press on
 * `far` (`updatePointer`'s `near` argument is always `false`) regardless of which mode is
 * active — it has no notion of mode to branch on — matching the single-button-does-both-jobs
 * reality of the trigger rather than keeping a second, pointer-only button meaning.
 *
 * Shift still exists, and still says which the mouse meant directly rather than leaving it to
 * whichever mode-scoped reach or aim test is live — a mouse click that lands *on* a node is
 * always going to read as "within reach" of it, since the cursor's pick point sits on the
 * node's own surface no matter how far the node actually is from the camera; nothing about a
 * flat click distinguishes "reaching for this" from "aiming at this from across the room" the
 * way a real hand's grip pose, genuinely close to the body, and its separate aim ray, genuinely
 * not, already do. So
 * `updateGrabber` reports a different `position` depending on `aiming`: the picked point under
 * the cursor when not aiming (so a plain click on a node reads as reach, exactly as before),
 * and the camera's own position when aiming (so a shift-click reads as nowhere near any node,
 * letting `nodeUnderAim`'s own `aimOrigin`/`forward` ray — untouched by any of this — decide
 * instead). That is a literal enactment of what a VR hand already does: reach happens near the
 * body, aim happens along a ray cast from it. No gesture logic is duplicated here — this file
 * only ever feeds the identical `Grabber` fields `PossessionManager`/`ConnectionDrawer` read.
 *
 * `v`/`m` are a deliberate exception to that last sentence: X and Y are controller-only in
 * production (`Grabber.talkButton`/`micToggleButton` are null for the desktop pointer, on
 * purpose — plan/ai-in-room.md), so there is no gesture for a key to forward to. These call
 * `AssistantController.beginTalk`/`endTalk`/`toggleMic` directly instead, the same way Tab and
 * `p` already call `authoring.palette.toggleInFront`/`cycleTool` directly rather than reading a
 * grabber field that would not exist for a mouse either. `v` is push-to-talk, so — unlike every
 * other key here — it is read on `keyup` as well as `keydown`: a real held button gives
 * `beginTalk`/`endTalk` a press and a release for free, and a keyboard key needs the second
 * listener to mean the same thing.
 *
 * A desktop client always joins as a spectator today (plan/roadmap.md, Phase 3 — main.ts
 * asserts the role from the splash's own entry choice, not from this class). `spectator`
 * (below) gates every authoring hotkey and the near/far grip out of this class once that role
 * is known, without deleting or reshaping any of it — the class stays exactly as usable to a
 * test as it always was, since every test in this file drives it with the field left at its
 * default (`false`).
 */

/** Where an unaimed pointer sits, for placing shapes in open space. */
const DEFAULT_REACH = 1.6;

export class DevSim {
  private gripping = false;
  private aiming = false;
  private pointerX = 0;
  private pointerY = 0;

  private readonly listeners: (() => void)[] = [];

  /**
   * Set once, after `hub.join()` resolves (main.ts) — not known at construction time, since
   * DevSim is built well before the splash's entry choice is known, and that choice is what
   * decides the role being asserted (plan/roadmap.md, Phase 3). Defaults `false` so
   * `DevSim.test.ts`'s existing
   * construction, and every gesture test that drives this class directly, keeps behaving exactly
   * as before — the class itself is unchanged, gated at the seam that decides whether these
   * hotkeys ever reach it, not deleted (CLAUDE.md, "the pointer authoring code stays, gated out
   * of the product").
   *
   * Gates the authoring hotkeys only — Tab (opens the tray, `AuthoringController.palette`),
   * Delete/Backspace (`deleteHeld`), `p` (`cycleTool`) — and the
   * pointer's own trigger signal that would otherwise let a drag grab, connect or draw. `v`/`m`
   * (the assistant's talk and mic-toggle keys) are untouched here: `m` stays live because
   * player-to-player voice
   * "serves the VR users equally" (plan/roadmap.md), and `v` is already inert for a spectator
   * inside `AssistantController.beginTalk` itself — gating it a second time here would just be
   * two copies of the same rule to keep in sync.
   */
  spectator = false;

  constructor(
    private readonly scene: Scene,
    private readonly canvas: HTMLCanvasElement,
    private readonly grabbers: GrabberHub,
    private readonly authoring: AuthoringController,
    private readonly assistant: AssistantController,
  ) {}

  start(): () => void {
    this.grabbers.pointerActive = true;

    this.on(this.canvas, 'pointermove', (event) => {
      const pointer = event as PointerEvent;
      this.pointerX = pointer.offsetX;
      this.pointerY = pointer.offsetY;
    });

    this.on(this.canvas, 'pointerdown', (event) => {
      const pointer = event as PointerEvent;

      if (pointer.button !== 0) {
        return;
      }

      this.pointerX = pointer.offsetX;
      this.pointerY = pointer.offsetY;

      // A spectator's click still moves the pointer position (tracked above, for whatever
      // reads `updateGrabber`'s output), but must not start a near/far grip — there is no
      // `AuthoringController.start()` tick running to consume one anyway (main.ts), but the
      // near/far state is still readable directly by a test, so it stays false here rather
      // than relying only on that absent consumer.
      if (this.spectator) {
        return;
      }

      this.aiming = pointer.shiftKey;
      this.gripping = true;
    });

    this.on(window, 'pointerup', () => {
      this.gripping = false;
    });

    this.on(window, 'keydown', (event) => {
      const key = event as KeyboardEvent;

      // Don't steal keys from a real DOM text field. Kept, though nothing in this class's own
      // reach currently has one live at the same time as this listener — SignIn's own
      // `HTMLInputElement`s exist only before this class is even constructed (main.ts builds
      // DevSim well after `form.waitForSignIn()` has already resolved). Harmless to leave as a
      // general-purpose guard.
      if (key.target instanceof HTMLInputElement || key.target instanceof HTMLTextAreaElement) {
        return;
      }

      // The authoring hotkeys — see `spectator`'s own doc for exactly what this does and does
      // not cover.
      if (this.spectator && (key.key === 'Tab' || key.key === 'Delete' || key.key === 'Backspace')) {
        key.preventDefault();
        return;
      }

      if (this.spectator && key.key.toLowerCase() === 'p') {
        return;
      }

      if (key.key === 'Tab') {
        key.preventDefault();
        this.authoring.palette.toggleInFront();
        return;
      }

      if (key.key === 'Delete' || key.key === 'Backspace') {
        key.preventDefault();
        void this.authoring.deleteHeld();
        return;
      }

      if (key.key.toLowerCase() === 'p') {
        key.preventDefault();
        this.authoring.cycleTool();
        return;
      }

      if (key.key.toLowerCase() === 'v') {
        key.preventDefault();

        // Held-key auto-repeat re-fires `keydown` every OS repeat interval while a real
        // controller button reports `pressed` continuously with no repeated `justPressed` —
        // `beginTalk` is already a no-op while a turn of this connection's own is open, so a
        // repeat here costs nothing, but `.repeat` still short-circuits it before that check
        // even runs.
        if (!key.repeat) {
          void this.assistant.beginTalk();
        }

        return;
      }

      if (key.key.toLowerCase() === 'm') {
        key.preventDefault();
        void this.assistant.toggleMic();
      }
    });

    this.on(window, 'keyup', (event) => {
      const key = event as KeyboardEvent;

      // Same guard as the `keydown` handler above, for the same reason — a text field owns
      // this keystroke instead. `endTalk` is already a no-op with no turn of this
      // connection's own open, so skipping the release here on that path is symmetrical with
      // `beginTalk` never having been reached on the matching press, not a new way to leave a
      // turn stuck open.
      if (key.target instanceof HTMLInputElement || key.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (key.key.toLowerCase() === 'v') {
        void this.assistant.endTalk();
      }
    });

    // Forwarded rather than handled here: only the tray's own carousel knows whether the
    // pointer is genuinely hovering one of its items (PaletteUI.scrollWheel's own "the depth
    // rule" — plan/tray-categories.md). preventDefault unconditionally, not only when the
    // palette consumes it: this canvas fills the viewport, and letting an un-consumed scroll
    // fall through to the page would just scroll a page that never shows.
    this.on(this.canvas, 'wheel', (event) => {
      const wheelEvent = event as WheelEvent;
      wheelEvent.preventDefault();

      if (this.spectator) {
        return;
      }

      this.authoring.palette.scrollWheel(wheelEvent.deltaY);
    });

    const observer = this.scene.onBeforeRenderObservable.add(() => this.updateGrabber());
    this.listeners.push(() => this.scene.onBeforeRenderObservable.remove(observer));

    return () => this.stop();
  }

  private updateGrabber(): void {
    const camera = this.scene.activeCamera;

    if (!camera) {
      return;
    }

    const ray = this.scene.createPickingRay(this.pointerX, this.pointerY, null, camera);

    // On the surface of whatever is under the cursor, so the grabber is inside the node's
    // reach and the near gesture responds. Nodes, open tray slots (including the carousel's
    // own items), and the whiteboard's own drawing surface — walls are not grabbable, and
    // without these in the list the cursor would sit a fixed 1.6m along the gaze ray, which
    // for the board (while a stroke is dragged across it) is rarely where the real surface
    // actually is.
    const pick = this.scene.pickWithRay(
      ray,
      (mesh) =>
        mesh.metadata?.nodeId !== undefined ||
        mesh.metadata?.trayPick === true ||
        mesh.metadata?.boardPick === true,
    );

    const pickedPosition =
      pick?.hit && pick.pickedPoint
        ? pick.pickedPoint
        : camera.globalPosition.add(ray.direction.scale(DEFAULT_REACH));

    // `aiming` (shift) is the mouse's own way of saying "I mean the far action" directly,
    // rather than trusting a reach test to work it out from where the cursor sits — see this
    // class's own module doc for why a flat click cannot leave that to proximity the way a
    // real hand can. Reporting the camera's own position here is what makes `nodeWithinReach`
    // (`GrabReach.ts`, still read by `PossessionManager.considerGrab` in Build mode) fail for
    // every node while aiming, exactly as it already does for a controller aiming at something
    // genuinely out of arm's reach: `position` stays near the body, `aimOrigin`/`forward`
    // (unchanged, always the camera and the gaze ray) are what actually reach the target.
    const position = this.aiming ? camera.globalPosition : pickedPosition;

    // Both gestures are the trigger now (this class's own module doc) — `near` is always
    // `false`, and `far` is the raw button state regardless of `aiming`, which only ever
    // changes `position` above.
    this.grabbers.updatePointer(position, camera.globalPosition, ray.direction, false, this.gripping);
  }

  /** Head and hand poses for a desktop peer, so remote avatars have something to show. */
  readPose(): { head: { p: number[]; r: number[] }; hand: { p: number[]; r: number[] } | null } | null {
    const camera = this.scene.activeCamera;

    if (!camera) {
      return null;
    }

    const rotation = camera.absoluteRotation;
    const pointer = this.grabbers.byHandedness('none');

    return {
      head: {
        p: vec3ToArray(camera.globalPosition),
        r: quatToArray(rotation),
      },

      // The cursor stands in for one hand, so a desktop peer still reads as pointing at
      // something rather than as a floating head.
      hand:
        pointer === undefined
          ? null
          : {
              p: vec3ToArray(pointer.position),
              r: quatToArray(rotation),
            },
    };
  }

  get isGripping(): boolean {
    return this.gripping;
  }

  private on(target: EventTarget, type: string, handler: (event: Event) => void): void {
    target.addEventListener(type, handler);
    this.listeners.push(() => target.removeEventListener(type, handler));
  }

  private stop(): void {
    this.grabbers.pointerActive = false;

    for (const remove of this.listeners.splice(0)) {
      remove();
    }
  }
}
